import React, { useEffect, useState } from 'react';
import { Upload, X, Plus, Tag as TagIcon, Trash2, Check, Pencil, Link as LinkIcon, Youtube } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import ResourceCard from '../resources/ResourceCard';
import { VideoModal } from '../resources/ResourceLibrary';
import resourcesApi, { Resource, ResourceTag, ResourceType, youtubeId, driveId } from '../../services/resourcesApi';
import '../../styles/mentee-dashboard.scss';
import '../plan/plan.scss';
import '../../styles/resources.scss';

const AdminResources: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [tags, setTags] = useState<ResourceTag[]>([]);
  const [tagFilter, setTagFilter] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [playing, setPlaying] = useState<Resource | null>(null);
  const [editing, setEditing] = useState<Resource | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [rs, ts] = await Promise.all([resourcesApi.list(), resourcesApi.tags()]);
      setResources(rs);
      setTags(ts);
    } catch (e) {
      console.error('Error loading resources:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (r: Resource) => {
    if (!window.confirm(`¿Eliminar “${r.title}”? Esta acción no se puede deshacer.`)) return;
    try { await resourcesApi.remove(r.id); load(); }
    catch (e) { console.error('Error deleting resource:', e); }
  };

  const visible = resources.filter(r => {
    if (tagFilter !== 'all' && !r.tags?.some(t => t.id === tagFilter)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return r.title.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q);
  });

  const countFor = (tagId: number) => resources.filter(r => r.tags?.some(t => t.id === tagId)).length;

  return (
    <MainLayout>
      <div className="mentee-dashboard sessions-view">
        <div className="sv-eyebrow">Administración</div>
        <h1 className="sv-title">Recursos del <em>Programa</em></h1>
        <p className="sv-lead">Publica el material del programa —archivos, videos y enlaces— y organízalo con etiquetas. Todo lo que subas aquí aparece al instante en la biblioteca de mentores y mentees.</p>

        <div className="sessions-page">
          {loading ? (
            <p className="dash-empty">Cargando recursos…</p>
          ) : (
            <>
              <div className="res-toolbar">
                <div className="res-chips">
                  <button className={`dupla-chip ${tagFilter === 'all' ? 'on' : ''}`}
                    onClick={() => setTagFilter('all')}>Todos ({resources.length})</button>
                  {tags.map(t => (
                    <button key={t.id} className={`dupla-chip ${tagFilter === t.id ? 'on' : ''}`}
                      onClick={() => setTagFilter(t.id)}>{t.name} ({countFor(t.id)})</button>
                  ))}
                </div>
                <div className="res-toolbar-right">
                  <input className="sf-input res-search" placeholder="Buscar recurso…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                  <button className="btn-ghost" onClick={() => setShowTags(true)}>
                    <TagIcon size={14} /> Etiquetas
                  </button>
                  <button className="btn-ink" onClick={() => setShowPublish(true)}>
                    <Plus size={15} /> Publicar recurso
                  </button>
                </div>
              </div>

              {visible.length === 0 ? (
                <p className="dash-empty">
                  {resources.length === 0
                    ? 'Aún no has publicado recursos. Usa “Publicar recurso” para subir un archivo, un enlace o un video.'
                    : 'Ningún recurso coincide con el filtro.'}
                </p>
              ) : (
                <div className="ses-cards res-cards">
                  {visible.map(r => (
                    <ResourceCard key={r.id} resource={r} onPlay={setPlaying}
                      onDownloaded={load} onDelete={handleDelete} onEdit={setEditing} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {showPublish && (
          <PublishModal tags={tags} onClose={() => setShowPublish(false)}
            onSaved={() => { setShowPublish(false); load(); }} />
        )}
        {showTags && (
          <TagsModal tags={tags} onClose={() => setShowTags(false)} onChanged={load} />
        )}
        {editing && (
          <EditModal resource={editing} tags={tags} onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }} />
        )}
        {playing && <VideoModal resource={playing} onClose={() => setPlaying(null)} />}
      </div>
    </MainLayout>
  );
};

// ------------------------------------------------------------- publicar

type Mode = 'file' | 'youtube' | 'link';

const PublishModal: React.FC<{ tags: ResourceTag[]; onClose: () => void; onSaved: () => void }> = ({ tags, onClose, onSaved }) => {
  const [mode, setMode] = useState<Mode>('file');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [type, setType] = useState<ResourceType>('document');
  const [file, setFile] = useState<File | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const toggleTag = (id: number) =>
    setPicked(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    setType(
      ['pdf', 'doc', 'docx', 'txt', 'ppt', 'pptx', 'xls', 'xlsx', 'csv'].includes(ext) ? 'document'
        : ['mp4', 'mov', 'webm', 'mpeg', 'avi'].includes(ext) ? 'video'
          : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? 'image' : 'other'
    );
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (title.trim().length < 3) { setError('El título debe tener al menos 3 caracteres'); return; }
    if (description.trim().length < 20) { setError('Escribe una descripción de al menos 20 caracteres: es lo que leerán mentores y mentees para decidir si les sirve'); return; }
    if (mode === 'file' && !file) { setError('Selecciona el archivo que quieres publicar'); return; }
    if (mode !== 'file' && !url.trim()) { setError('Pega la URL del recurso'); return; }
    if (mode === 'youtube' && !youtubeId(url) && !driveId(url)) {
      setError('Pega la URL de un video de YouTube o de un archivo de Google Drive'); return;
    }

    try {
      setBusy(true);
      if (mode === 'file') {
        const fd = new FormData();
        fd.append('file', file as File);
        fd.append('title', title);
        fd.append('description', description);
        fd.append('type', type);
        fd.append('tag_ids', picked.join(','));
        await resourcesApi.upload(fd, setProgress);
      } else {
        await resourcesApi.createLink({
          title, description,
          type: mode === 'youtube' ? 'video' : 'link',
          url: url.trim(),
          tag_ids: picked,
        });
      }
      onSaved();
    } catch (err: any) {
      const messages = err?.response?.data?.messages;
      setError(messages ? Object.values(messages).join(' · ') : (err?.response?.data?.message || 'No se pudo publicar el recurso'));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal sf-modal" onClick={e => e.stopPropagation()}>
        <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <h3>Publicar un recurso</h3>
        <p className="feedback-modal__sub">Queda visible para todos los mentores y mentees del programa.</p>

        {error && <div className="sf-empty">{error}</div>}

        <div className="sf-label">¿Qué vas a publicar?</div>
        <div className="res-mode-cards">
          <button type="button" className={`sf-mod-card ${mode === 'file' ? 'on' : ''}`} onClick={() => setMode('file')}>
            <div className="sf-mod-t"><Upload size={15} /> Un archivo</div>
            <div className="sf-mod-d">PDF, Office, imagen o video mp4 (máx. 200 MB).</div>
          </button>
          <button type="button" className={`sf-mod-card ${mode === 'youtube' ? 'on' : ''}`} onClick={() => setMode('youtube')}>
            <div className="sf-mod-t"><Youtube size={15} /> Video (YouTube o Drive)</div>
            <div className="sf-mod-d">Se reproduce dentro de la plataforma, sin salir del sitio.</div>
          </button>
          <button type="button" className={`sf-mod-card ${mode === 'link' ? 'on' : ''}`} onClick={() => setMode('link')}>
            <div className="sf-mod-t"><LinkIcon size={15} /> Un enlace</div>
            <div className="sf-mod-d">Artículo, carpeta compartida u otro sitio.</div>
          </button>
        </div>

        <form onSubmit={submit}>
          {mode === 'file' ? (
            <>
              <div className="sf-label">Archivo</div>
              <input className="sf-input" type="file" onChange={pickFile} />
            </>
          ) : (
            <>
              <div className="sf-label">{mode === 'youtube' ? 'URL del video' : 'URL'}</div>
              <input className="sf-input" type="url" value={url} onChange={e => setUrl(e.target.value)}
                placeholder={mode === 'youtube' ? 'https://www.youtube.com/watch?v=…  o  https://drive.google.com/file/d/…' : 'https://…'} />
            </>
          )}

          <div className="sf-label">Título</div>
          <input className="sf-input" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="Nombre con el que lo verán mentores y mentees" />

          <div className="sf-label">Descripción <span className="sf-hint">la ven mentores y mentees en la tarjeta</span></div>
          <textarea className="sf-input" rows={3} style={{ resize: 'vertical' }}
            value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Qué contiene y en qué momento del programa conviene usarlo. Ej.: “Grabación del taller de apertura: el rol del mentor, cómo abrir la relación y conducir las primeras conversaciones.”" />

          {mode === 'file' && (
            <>
              <div className="sf-label">Tipo</div>
              <select className="sf-input" value={type} onChange={e => setType(e.target.value as ResourceType)}>
                <option value="document">Documento</option>
                <option value="video">Video</option>
                <option value="image">Imagen</option>
                <option value="other">Otro</option>
              </select>
            </>
          )}

          <div className="sf-label">Etiquetas <span className="sf-hint">se administran con el botón “Etiquetas”</span></div>
          {tags.length === 0 ? (
            <div className="sf-empty">Todavía no hay etiquetas. Créalas desde “Etiquetas” para poder clasificar.</div>
          ) : (
            <div className="res-chips">
              {tags.map(t => (
                <button key={t.id} type="button"
                  className={`dupla-chip ${picked.includes(t.id) ? 'on' : ''}`}
                  onClick={() => toggleTag(t.id)}>{t.name}</button>
              ))}
            </div>
          )}

          {busy && progress > 0 && (
            <div className="res-progress"><div className="res-progress-fill" style={{ width: `${progress}%` }} /></div>
          )}

          <div className="feedback-modal__actions">
            <button type="button" className="btn btn--outline btn--small" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn--primary btn--small" disabled={busy}>
              {busy ? 'Publicando…' : 'Publicar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- etiquetas

const TagsModal: React.FC<{ tags: ResourceTag[]; onClose: () => void; onChanged: () => void }> = ({ tags, onClose, onChanged }) => {
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<any>) => {
    setError('');
    setBusy(true);
    try { await fn(); onChanged(); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo guardar el cambio'); }
    finally { setBusy(false); }
  };

  const add = async () => {
    if (!newName.trim()) return;
    await run(() => resourcesApi.createTag(newName.trim()));
    setNewName('');
  };

  const rename = async (id: number) => {
    if (!editName.trim()) return;
    await run(() => resourcesApi.renameTag(id, editName.trim()));
    setEditing(null);
  };

  const remove = async (t: ResourceTag) => {
    if (!window.confirm(`¿Eliminar la etiqueta “${t.name}”? Los recursos se conservan, solo pierden la etiqueta.`)) return;
    await run(() => resourcesApi.deleteTag(t.id));
  };

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal sf-modal" onClick={e => e.stopPropagation()}>
        <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <h3>Etiquetas de recursos</h3>
        <p className="feedback-modal__sub">Sirven para clasificar y filtrar la biblioteca. Puedes crearlas, renombrarlas o eliminarlas.</p>

        {error && <div className="sf-empty">{error}</div>}

        <div className="sf-label">Nueva etiqueta</div>
        <div className="res-tag-new">
          <input className="sf-input" value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Por ejemplo: Liderazgo" />
          <button className="btn-ink" onClick={add} disabled={busy || !newName.trim()}>
            <Plus size={14} /> Agregar
          </button>
        </div>

        <div className="sf-label">Etiquetas existentes</div>
        {tags.length === 0 ? (
          <div className="sf-empty">Todavía no hay etiquetas.</div>
        ) : (
          <div className="res-tag-list">
            {tags.map(t => (
              <div className="res-tag-row" key={t.id}>
                {editing === t.id ? (
                  <>
                    <input className="sf-input" value={editName} autoFocus
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); rename(t.id); } }} />
                    <button className="btn-ghost btn-icon" onClick={() => rename(t.id)} title="Guardar"><Check size={14} /></button>
                    <button className="btn-ghost btn-icon" onClick={() => setEditing(null)} title="Cancelar"><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <span className="res-tag-name">{t.name}</span>
                    <span className="res-tag-count">{t.resources_count || 0} recurso(s)</span>
                    <button className="btn-ghost" onClick={() => { setEditing(t.id); setEditName(t.name); }} title="Renombrar">
                      <Pencil size={14} />
                    </button>
                    <button className="btn-ghost btn-icon" onClick={() => remove(t)} title="Eliminar">
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="feedback-modal__actions">
          <button className="btn btn--primary btn--small" onClick={onClose}>Listo</button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- editar

const EditModal: React.FC<{ resource: Resource; tags: ResourceTag[]; onClose: () => void; onSaved: () => void }> =
  ({ resource, tags, onClose, onSaved }) => {
    const [title, setTitle] = useState(resource.title);
    const [description, setDescription] = useState(resource.description || '');
    const [picked, setPicked] = useState<number[]>((resource.tags || []).map(t => t.id));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const toggleTag = (id: number) =>
      setPicked(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

    const save = async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      if (title.trim().length < 3) { setError('El título debe tener al menos 3 caracteres'); return; }
      if (description.trim().length < 20) { setError('La descripción debe tener al menos 20 caracteres'); return; }
      try {
        setBusy(true);
        await resourcesApi.update(resource.id, {
          title: title.trim(), description: description.trim(), tag_ids: picked,
        });
        onSaved();
      } catch (err: any) {
        const messages = err?.response?.data?.messages;
        setError(messages ? Object.values(messages).join(' · ') : (err?.response?.data?.message || 'No se pudo guardar'));
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="feedback-modal-overlay">
        <div className="feedback-modal sf-modal" onClick={e => e.stopPropagation()}>
          <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
          <h3>Editar recurso</h3>
          <p className="feedback-modal__sub">Cambia cómo se presenta en la biblioteca. El archivo o el enlace no se modifican.</p>

          {error && <div className="sf-empty">{error}</div>}

          <form onSubmit={save}>
            <div className="sf-label">Título</div>
            <input className="sf-input" value={title} onChange={e => setTitle(e.target.value)} />

            <div className="sf-label">Descripción <span className="sf-hint">la ven mentores y mentees en la tarjeta</span></div>
            <textarea className="sf-input" rows={3} style={{ resize: 'vertical' }}
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Qué contiene y en qué momento del programa conviene usarlo." />

            <div className="sf-label">Etiquetas</div>
            {tags.length === 0 ? (
              <div className="sf-empty">Todavía no hay etiquetas.</div>
            ) : (
              <div className="res-chips">
                {tags.map(t => (
                  <button key={t.id} type="button"
                    className={`dupla-chip ${picked.includes(t.id) ? 'on' : ''}`}
                    onClick={() => toggleTag(t.id)}>{t.name}</button>
                ))}
              </div>
            )}

            <div className="feedback-modal__actions">
              <button type="button" className="btn btn--outline btn--small" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn--primary btn--small" disabled={busy}>
                {busy ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

export default AdminResources;
